/**
 * QuestionCycleHeader Component
 * Displays a subtle horizontal divider with centered label for question cycle groups
 * Used to visually separate question cycles in ConversationPanel
 *
 * BUG-20260224-175643: Removed checkmark from header. The header is now a pure
 * label-only divider. The completion checkmark has moved to a footer rendered at
 * the BOTTOM of each completed cycle in ConversationPanel. This prevents the
 * premature checkmark appearance where the checkmark was visible at the top of a
 * cycle before any messages (or answers) were shown below it. - 2026-02-24
 */

import { cn } from '@/lib/utils';

/**
 * Props for QuestionCycleHeader component
 */
export interface QuestionCycleHeaderProps {
  /** Label text to display (e.g., 'Question 1 of 5' or 'Introduction') */
  label: string;
  /** Whether this question cycle is complete.
   * Kept for backward compatibility but no longer renders a checkmark in the header.
   * The checkmark is now shown at the bottom of completed cycles in ConversationPanel. */
  isComplete?: boolean;
}

/**
 * QuestionCycleHeader - Subtle separator with label for question cycles
 *
 * Features:
 * - Horizontal divider lines with centered label text
 * - Muted earth tone styling (calm, non-intrusive)
 * - Accessible with ARIA separator role
 * - Handles empty/long labels gracefully
 *
 * @example
 * ```tsx
 * <QuestionCycleHeader label="Question 1 of 5" />
 * <QuestionCycleHeader label="Introduction" />
 * ```
 */
export function QuestionCycleHeader({ label }: QuestionCycleHeaderProps) {
  // Return nothing if label is empty
  if (!label || label.trim() === '') {
    return null;
  }

  return (
    <div className="flex items-center gap-3 my-4" role="separator" aria-label={label}>
      {/* Left divider line */}
      <div className="flex-1 h-px bg-[#E7D8C8]" />

      {/* Centered label as a warm chip pill — no checkmark; completion indicator is at bottom of cycle */}
      <span
        className={cn(
          'font-display text-xs font-medium whitespace-nowrap text-ellipsis overflow-hidden max-w-[200px]',
          'rounded-full border border-otter-sand bg-white px-3 py-1 text-otter-earth-700 shadow-sm'
        )}
      >
        {label}
      </span>

      {/* Right divider line */}
      <div className="flex-1 h-px bg-[#E7D8C8]" />
    </div>
  );
}
